import * as React from 'react';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { generatePath } from "react-router";
import { Size, useSize } from 'react-hook-size';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { Button } from "react-bootstrap";
import { isAbortError } from "../../../../../utils/errorUtils";

import moment from 'moment';
import { Definition } from "../../../../../data/Definition";
import '../../../../../styles/mapbox-gl.css'
import '../../../styles/FieldManager.scss';

import AboveWidget from './AboveWidget';

import { GET_PROPERTY_UNITS } from '../../../../../queries';
import { GetPropertyUnits } from '../../../../../apps/fields/types/types';
import { useUnitConversion, formatUnitAbbreviation} from '../../../utils/unitConversion';

import _ from "lodash";

import DeckGL from '@deck.gl/react';
import { NavigationControl, ScaleControl, Popup, Source, Layer } from 'react-map-gl';
import ZoomExtentTool from '../../fields/components/ZoomExtentTool';
import LayerVisibilityControlPanel from '../../fields/components/LayerVisibilityControlPanel';
import * as turf from '@turf/turf';
import ESRI_BASE from '../../../components/Map/styles/esri-base.json';
import { ViewState } from 'react-map-gl-draw/node_modules/@types/react-map-gl';
import { IDeckGlLayer, MapLayer } from '../../../deckgl/MapLayer';
import GLMapLayers from "./GLMapLayers";
import useMapLayers from '../hooks/useMapLayers';
//import StackedSoilLayer from './StackedSoilLayer';
import SoilInfoWidget from '../../fields/components/show/SoilInfoWidget';
import L2ImagerySourceLayer from './L2ImagerySourceLayer';
import BaseMapLayers from '../../../components/BaseMapLayers';
import { SETTING_SEASON_YEAR } from "../../../../../apps/fields/utils/settings";
import { useLayerStore } from '../../fields/providers/LayerProvider';
import { useFieldStore } from '../../fields/providers/FieldProvider';
import { queryDwPointBBCardMaxMinDataStats, queryDwPointBBCardMaxMinDataNumber, queryDwPointBBCardMaxMinDataNumberBI } from '../../../utils/dwHelpers';
import SoilDataSourceLayer from '../../soils/components/SoilDataSourceLayer';

import { useRasterDataSources } from '../../../hooks/useRasterDataSources';
import { Field, PlantProfile, DataSourceLayerMeta, DataSourceRasterLayer, LayerVisibility } from '../../../types/types';
import InfoDrawer from '../../../components/Elements/InfoDrawer';
import FieldDownload from '../../../features/fields/components/show/FieldDownload';

import { useQuery } from 'react-apollo';

import { zoomToFeature, zoomToFeatures, createFeatureFromField, createFeatureCollection, getViewport, createCentroidFeatureFromField } from '../../../utils/mapUtils';

import { useGetAllFields } from '../../../api/getAllFields';
import { StaticMap, MapContext } from 'react-map-gl';
import FieldList from '../../../components/FieldList';
import { 
    NAIP_DATA_SOURCE, 
    ESRI_DATA_SOURCE, 
    MAPBOX_DATA_SOURCE,
    ESRI_DATA_SOURCE_LABELS,
    HIGH_RES_COLOR,
    HIGH_RES_TWI, 
    HIGH_RES_SLOPE, 
    HIGH_RES_PROFILECURVATURE, 
    HIGH_RES_PLANIMETRICCURVATURE, 
    HIGH_RES_ELEVATION, 
    HIGH_RES_ASPECT,
} from '../../../constants';
import { useStore } from '../../../providers/AppProvider';
import { date } from 'yup';

const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;


export const PlantsView = () => {
    const { state, orgId } = useStore();
    const [ RASTER_DATA_SOURCES,  set_RASTER_DATA_SOURCES ] = useState<DataSourceLayerMeta[]>([{
            id: NAIP_DATA_SOURCE,
            type: 'base',
            name: 'Historical Imagery',
            order: 7,
            visible: false,
            minZoom: 10,
            maxZoom: 24
        },{
            id: MAPBOX_DATA_SOURCE,
            type: 'base',
            name: 'Mapbox Imagery',
            order: 8,
            visible: true,
            minZoom: 0,
            maxZoom: 24
        },{
            id: ESRI_DATA_SOURCE_LABELS,
            type: 'base',
            name: 'Labels',
            order: 0,
            visible: true,
            minZoom: 0,
            maxZoom: 18
        },{
            id: `${orgId}${HIGH_RES_COLOR}`,
            disambiguate:'High Res. Normal Color',
            name:'UAS Imagery',
            type: 'base',
            order: 0,
            visible: true,
            minZoom: 13,
            maxZoom: 24
        },
        {
            id: `${orgId}${HIGH_RES_TWI}`,
            name:'Topographic Wetness Index',
            date: 'season',
            type: 'base',
            order: 1,
            visible: false,
            minZoom: 13,
            maxZoom: 24
        },
        {
            id: `${orgId}${HIGH_RES_SLOPE}`,
            name:'Slope',
            date: 'season',
            type: 'base',
            order: 2,
            visible: false,
            minZoom: 13,
            maxZoom: 24
        },
        {
            id: `${orgId}${HIGH_RES_PROFILECURVATURE}`,
            name:'Profile Curvature',
            date: 'season',
            type: 'base',
            order: 3,
            visible: false,
            minZoom: 13,
            maxZoom: 24
        },
        {
            id: `${orgId}${HIGH_RES_PLANIMETRICCURVATURE}`,
            name:'Planimetric Curvature',
            date: 'season',
            type: 'base',
            order: 4,
            visible: false,
            minZoom: 13,
            maxZoom: 24
        },
        {
            id: `${orgId}${HIGH_RES_ELEVATION}`,
            name:'Elevation',
            date: 'season',
            type: 'base',
            order: 5,
            visible: false,
            minZoom: 13,
            maxZoom: 24
        },
        {
            id: `${orgId}${HIGH_RES_ASPECT}`,
            name:'Aspect',
            date: 'season',
            type: 'base',
            order: 6,
            visible: false,
            minZoom: 13,
            maxZoom: 24
        },
    ]);
    const [plantsCounter, setPlantsCounter] = useState<number>(0);
    const [horizonSelected, setHorizonSelected] = useState<string | null>(null);
    const [propertySelected, setPropertySelected] = useState<string | null>(null);
    const [overlayVisibility, setOverlayVisible] = useState<LayerVisibility[]>([]);
    const [rasterDataSources, setRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [bbCardDataSources, setBbCardDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [bbCardDataCounter, setBbCardDataCounter] = useState<number>(0);
    const [updateSoilsKey, setUpdateSoilsKey] = useState<number>(0);
    const [readyToLoadSoilHorizonData, setReadyToLoadSoilHorizonData] = useState<boolean>(false);
    const { dataSources, loading } = useRasterDataSources(RASTER_DATA_SOURCES);
    const { data: propertyUnitsData, loading: propertyUnitsLoading } = useQuery(GET_PROPERTY_UNITS, {
        variables: {org_id: orgId },
    });

    const [showDownload, setShowDownload] = useState(false);

    const history = useHistory();
    const match = useRouteMatch(`${FIELD_APP_URL}/:vp/plants`);
    const { vp, field } = useParams();

    const mapContainerRef = useRef(null);
    const { width, height } = useSize(mapContainerRef);

    const { localePreferences } = useStore();
    const { convertToPreferredUnitValue, convertToPreferredUnitString, unitsConvert } = useUnitConversion();

    let { data: fields } = useGetAllFields(); // This will get the unfiltered fields
    let { fields: fieldsFiltered } = useFieldStore();

    const { rasterLayers, clearLayers } = useLayerStore();

    const [viewport, setViewport] = useState<ViewState>(getViewport(vp));
    const [selected, setSelected] = useState<number>(0);

    // L2 rasters, animation, and timeline 
    const [animationRasterDataSources, setAnimationRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [UASImageryAnimationRasterDataSources, setUASImageryAnimationRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [staticRasterDataSources, setStaticRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [datesAvailable, setDatesAvailable] = useState<Array<string>>([])
    const [filteredDatesAvailable, setFilteredDatesAvailable] = useState<Array<string>>([])
    const [timelineStatus, setTimelineStatus] = useState<string>("");
    const [activeAnimation, setActiveAnimation] = useState<string>("not engaged"); // "not engaged" | "playing" | "paused"
    const [loadedTiles, setLoadedTiles] = useState<boolean>(true);
    const [timerInterval, setTimerInterval] = useState<number>(0);
    const [dateString, setDateString] = useState<string | null>(null);
    const [indexA, setIndexA] = useState<number>(0);
    const [animationInterval, setAnimationInterval] = useState<any>(undefined);
    const [dateSelectorParameters, setDateSelectorParameters] = useState<any>(null);
    const [plantDateIndexSelected, setPlantDateIndexSelected] = useState<Array<number>>([0,0,0]);
    const [dateArrayIndexSelected, setDateArrayIndexSelected] = useState<number>(0);
    const [dateArrayDateStringSelected, setDateArrayDateStringSelected] = useState<string>("");
    const [visible, setVisible] = useState<boolean>(true);
    const [visibleDW, setVisibleDW] = useState<boolean>(false);
    const [dwAndRasterTimelineDate, setDwAndRasterTimelineDate] = useState<string | null>(null); 
    const [dwAndRasterTimelineDateComplete, setDwAndRasterTimelineDateComplete] = useState<boolean>(false); 
    const [dwBBCardMinMaxData, setDwBBCardMinMaxData] = useState<any>({}); 

    // used to help refresh the layers
    const [, setRefreshAt] = useState<number>(0);
 
    // fields
    const [hoverFieldId, setHoverFieldId] = useState<number | null>(null);
    const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
    const [selectedField, setSelectedField] = useState<Field | null>(null);
    const [selectedFieldIncrement, setSelectedFieldIncrement] = useState<number>(0);
    const [map, setMap] = useState<any | null>(null);
    const [fieldsDataSource, setFieldsDataSource] = useState(null);
    const [centroidDataSource, setCentroidDataSource] = useState(null);

    // Deck.gl
    const { mapLayers, addMapLayers, addMapLayer, clearMapLayers, removeMapLayer } = useMapLayers();

    // points / right bb card 
    const [selectedPlantId, setSelectedPlantId] = useState<number | null>(null);
    const [selectedPlantName, setSelectedPlantName] = useState<string | null>(null);
    const [selectedPlantCol, setSelectedPlantCol] = useState<string | null>(null);
    const [selectedPlantRow, setSelectedPlantRow] = useState<string | null>(null);
    const [selectedPlantObj, setSelectedPlantObj] = useState<any | null>(null);
    const [selectedPlantProfile, setSelectedPlantProfile] = useState<PlantProfile | null>(null);
    const [hoverInfo, setHoverInfo] = useState<any>(null);
    const [rightInfoDrawerOpen, setRightInfoDrawerOpen] = useState<boolean>(true);

    const [size, setSize] = useState<Size>({
        width: width || 800,
        height: height || 800,
    });

    // related to soil horizon layers
    const [soilRasterDataSources, setSoilRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [soilRasterVisibility, setSoilRasterVisibility] = useState<boolean>(false);
    const [selectedSeason, setSelectedSeason] = useState<string>("2022");
    const [preferredLenSmall, setPreferredLenSmall] = useState<any>(null);
    const [preferredLenAbbr, setPreferredLenAbbr] = useState<any>("in");
    const { settings, socket, updateFieldSelected, updateLayersForExport, setDownloadDateRange } = useStore();

    useEffect(() => {
        const _preferredLenSmall = _.get(localePreferences, 'measureUnits.length.small');
        const _preferredLenAbbr: string =
            typeof _preferredLenSmall === 'string' ? _preferredLenSmall : _preferredLenSmall?.abbr || 'in';
        setPreferredLenSmall(_preferredLenSmall);
        setPreferredLenAbbr(_preferredLenAbbr);
    }, [localePreferences]);

    useEffect(() => {
        if (dataSources){
            console.log("BB012524 RASTER_DATA_SOURCES", RASTER_DATA_SOURCES)
            console.log("BB012524 dataSources", dataSources)
            console.log("BB012524 overlayVisibility", overlayVisibility)
            overlayVisibility.forEach((item) => {
                set_RASTER_DATA_SOURCES(RASTER_DATA_SOURCES.map((itemR) => {
                    //console.log("BB012524 item", item)
                    //console.log("BB012524 itemR", itemR)
                    if (itemR.id === item.layer_id) {
                        itemR.visible = item.is_visible;
                        return itemR;
                    }
                    if (itemR.id === item.layer_id + 'n') { //specificlally for -rgbn to match -rgb
                        itemR.visible = item.is_visible;
                        return itemR;
                    }
                    return itemR;
                }))
            })
            console.log("BB012524 RASTER_DATA_SOURCES", RASTER_DATA_SOURCES)
        }
    }, [ overlayVisibility ]);


    useEffect(() => {
        console.log("090123 socket", socket);
    }, [socket]);

    useEffect(() => {
        console.log("B091725 useEffect(() => settings[SETTING_SEASON_YEAR]", settings[SETTING_SEASON_YEAR]);
        console.log("B091725 SETTING_SEASON_YEAR", SETTING_SEASON_YEAR);
        setSelectedSeason(settings[SETTING_SEASON_YEAR]);
    }, [settings[SETTING_SEASON_YEAR]]);

    useEffect(() => {
        console.log("B091725 useEffect(() => selectedSeason, selectedField", selectedSeason, selectedField);
        console.log("B091725 settings[SETTING_SEASON_YEAR]", settings[SETTING_SEASON_YEAR]);
        console.log("B091725 SETTING_SEASON_YEAR", SETTING_SEASON_YEAR);
        const controller = new AbortController();
        let definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('plant'));
        let definitionField = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('field'));
        if (!definition) {
            definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('tree'));
        }
        if (definition && definitionField && selectedField && selectedSeason) {
            const fetchData = async () => {
                console.log("071625 selectedField", selectedField);
                console.log("071625 definition", definition);
                console.log("071625 definitionField", definitionField);
                console.log("080224 selectedSeason", selectedSeason);
                const dtStart = `${selectedSeason}-01-01 00:00:00.00`;
                const dtEnd = `${selectedSeason}-12-31 23:59:59.59`;
                try {
                    // Execute all min/max data loading operations in parallel
                    const [dataMinStats, dataMaxStats, dataMinNumber, dataMaxNumber, dataMinNumberBI, dataMaxNumberBI] = await Promise.all([
                        queryDwPointBBCardMaxMinDataStats(selectedField.entityId, definition as Definition, definitionField as Definition, "min", dtStart, dtEnd, controller.signal),
                        queryDwPointBBCardMaxMinDataStats(selectedField.entityId, definition as Definition, definitionField as Definition, "max", dtStart, dtEnd, controller.signal),
                        queryDwPointBBCardMaxMinDataNumber(selectedField.entityId, definition as Definition, definitionField as Definition, "min", dtStart, dtEnd, controller.signal),
                        queryDwPointBBCardMaxMinDataNumber(selectedField.entityId, definition as Definition, definitionField as Definition, "max", dtStart, dtEnd, controller.signal),
                        queryDwPointBBCardMaxMinDataNumberBI(selectedField.entityId, definition as Definition, definitionField as Definition, "min", dtStart, dtEnd, controller.signal),
                        queryDwPointBBCardMaxMinDataNumberBI(selectedField.entityId, definition as Definition, definitionField as Definition, "max", dtStart, dtEnd, controller.signal)
                    ]);
                    
                    console.log("All min/max data loading operations completed");
                    // Process data as needed
                    //console.log("B091725 dataMinStats", dataMinStats);
                    //console.log("B091725 dataMaxStats", dataMaxStats);
                    //console.log("B091725 dataMinNumber", dataMinNumber);
                    //console.log("B091725 dataMaxNumber", dataMaxNumber);
                    //console.log("B091725 dataMinNumberBI", dataMinNumberBI);
                    //console.log("B091725 dataMaxNumberBI", dataMaxNumberBI);
                    setDwBBCardMinMaxData({
                        dataMinStats: dataMinStats,
                        dataMaxStats: dataMaxStats,
                        dataMinNumber: dataMinNumber,
                        dataMaxNumber: dataMaxNumber,
                        dataMinNumberBI: dataMinNumberBI,
                        dataMaxNumberBI: dataMaxNumberBI,
                    });
                    
                    // Only proceed with plant profile updates after data is set
                    console.log("B091725 selectedPlantProfile", selectedPlantProfile);
                    console.log("A091825 settings[SETTING_SEASON_YEAR]", settings[SETTING_SEASON_YEAR]);
                    console.log("A091825 readyToLoadSoilHorizonData", readyToLoadSoilHorizonData);
                    console.log("B091725 dateString", dateString);
                    //if (readyToLoadSoilHorizonData){
                    //    console.log("AA092225 setReadyToLoadSoilHorizonData(false), readyToLoadSoilHorizonData", readyToLoadSoilHorizonData)
                        //setReadyToLoadSoilHorizonData(false); // This is likely uneeded, because PlantView remounts apparently
                    //}
                    if (selectedPlantProfile && selectedPlantProfile.plantProfileYear !== settings[SETTING_SEASON_YEAR]){
                        setSelectedPlantProfile(prev => (prev ? { ...prev, plantProfileYear: settings[SETTING_SEASON_YEAR] } : prev))
                    }
                } catch (error) {
                    if (!isAbortError(error)) {
                        console.error('Failed to load canopy stats data:', error);
                    }
                }
            };
            
            // Wait for fetchData to complete before proceeding
            fetchData();
        }
        /*return () => {
            controller.abort();
        };*/
    }, [selectedSeason, selectedField]);

    useEffect(() => {
        console.log("soilRasterDataSources", soilRasterDataSources);
    }, [soilRasterDataSources]);


    useEffect(() => {
        //setLoading(cropLoading || varietyLoading || definitionLoading);
        console.log("112823 propertyUnitsData", propertyUnitsData);
    }, [propertyUnitsLoading, propertyUnitsData]);

    useEffect(() => {
        //console.log("timerInterval", timerInterval)
        //console.log("activeAnimation", activeAnimation)
        if (activeAnimation === "playing"){
            //console.log("indexA",indexA);
            //console.log("datesAvailable",datesAvailable);
            //console.log("filteredDatesAvailable",filteredDatesAvailable);
            let s = filteredDatesAvailable[indexA];
            let x = animationRasterDataSources.map((source: DataSourceRasterLayer, sourceIndex:number) => {
                if (source.dateString === s){

                    source.opacity = 1;
                }else{
                    source.opacity = 0;
                }
                return source;
            })
            let y = UASImageryAnimationRasterDataSources.map((source: DataSourceRasterLayer, sourceIndex:number) => {
                if (source.dateString === s){
                    source.opacity = 1;
                }else{
                    source.opacity = 0;
                }
                return source;
            })
            //console.log("s", s);
            setTimelineStatus(s);
            setAnimationRasterDataSources(x);
            setUASImageryAnimationRasterDataSources(y);
            if (indexA + 1 >= filteredDatesAvailable.length){
                setActiveAnimationFromParent("paused");
                clearInterval(animationInterval);
            }else{
                setIndexA(prev => prev + 1 )
            }
        }else{
            clearInterval(animationInterval);
        }
    }, [timerInterval]);

    useEffect(() => {
        if (!loadedTiles){
            assessAnimationLoad();
        }else{
            //
        }
    }, [loadedTiles]);

    useEffect(() => {
        if (!loading && dataSources) {
            const baseSources: DataSourceRasterLayer[] = dataSources.filter((d: DataSourceRasterLayer) => d.type == "base")
            const baseLayers: LayerVisibility[] = baseSources.map((d: DataSourceRasterLayer) => {
                return {
                    layer_id: d.id,
                    layer_name: d.name,
                    is_visible: d.is_visible,
                    order: d.order,
                    source: d.source
                };
            });

            //const UASImagery = baseSources.filter((d: DataSourceRasterLayer) => d.name == "UAS Imagery")[0]
            setOverlayVisible(baseLayers);
    
            setRasterDataSources(dataSources);
            setBbCardDataSources(dataSources.filter((d: DataSourceRasterLayer) => d.name == "High Res. Vegetation Index"));
        }
    }, [dataSources, loading]);

    useEffect(() => {
        if (dateSelectorParameters && plantDateIndexSelected){
            //console.log("071925 dateSelectorParameters", dateSelectorParameters);
            //console.log("071925 plantDateIndexSelected", plantDateIndexSelected);
            //console.log("071925 selected", selected);
            //console.log("071925 indexA", indexA);
            let _sequence = dateSelectorParameters.sequence;
            let min_date = dateSelectorParameters.min_date;
            let max_date = dateSelectorParameters.max_date;
            for (let s in _sequence){
                if (parseInt(_sequence[s][0]) == plantDateIndexSelected[0]){
                    min_date = s;
                } 
                if (parseInt(_sequence[s][0]) == plantDateIndexSelected[2]){
                    max_date = s;
                } 
            };
            let momentDateMin = moment(min_date).format('YYYY-MM-DD');
            var minDate:any = new Date(momentDateMin);
            var minDaysSinceEpoch = Math.floor(minDate/8.64e7);
            let momentDateMax = moment(max_date).format('YYYY-MM-DD');
            var maxDate:any = new Date(momentDateMax);
            var maxDaysSinceEpoch = Math.floor(maxDate/8.64e7);
            setDownloadDateRange({
                "yearStart": minDaysSinceEpoch,
                "min_date": min_date,
                "yearEnd": maxDaysSinceEpoch,
                "max_date": max_date,
            });
        }
        
    }, [dateSelectorParameters, plantDateIndexSelected]);

    useEffect(() => {
        //console.log("AA053024 dateSelectorParameters", dateSelectorParameters);
        //console.log("AA053024 plantDateIndexSelected", plantDateIndexSelected);
        //console.log("AA053024 selected", selected);
        //console.log("AA053024 indexA", indexA);
        if (dateSelectorParameters && plantDateIndexSelected){
            const findPosition = (data:any, value:Number) => {
                const keys = Object.keys(data);
                for (let i = 0; i < keys.length; i++) {
                    /*console.log("AA053024 data[keys[i]]", data[keys[i]]);
                    console.log("AA053024 keys[i]", keys[i]);
                    console.log("AA053024 value", value);
                    console.log("AA053024 keys.length", keys.length);
                    console.log("AA053024 i", i);*/
                    if (data[keys[i]].includes(value)) {
                        return keys.length - (i + 1);
                        //return i;
                    }
                }
                return 0; // Return -1 if the value is not found
            }
            
            const position = findPosition(dateSelectorParameters.sequence, plantDateIndexSelected[1]);
            //console.log("AA053024 position",position);
            setDateArrayDateStringSelected(dateSelectorParameters.dates[position]);
            //setDateArrayIndexSelected(position);
        }
    }, [dateSelectorParameters, plantDateIndexSelected, indexA, selected]);

    useEffect(() => {
        if (width && height) {
            setSize({ width, height });
        }
    }, [width, height]);
    
    // set selected field when the field id is in the url
    useEffect(() => {
        //console.log("071825 fields", fields);
        //console.log("071825 field", field);
        if (fields && field) {
            const selectedField = fields.find((f:any) => f.id == field);
            console.log("B082423  selectedField, width, height", selectedField, width, height);
            if (selectedField /*&& width && height*/) {
                console.log("B082423 selectedField, width, height", selectedField, width, height);
                setSelectedFieldId(selectedField.id ? selectedField.id : null);
                setSelectedField(selectedField);
            }
        }
    }, [fields]);

    useEffect(() => {
        //console.log("A071925 fieldsFiltered", fieldsFiltered);
        //console.log("A071925 plantDateIndexSelected", plantDateIndexSelected);
        //console.log("A071925 dateSelectorParameters", dateSelectorParameters);
        //console.log("A071925 dateSelectorParameters.marks[plantDateIndexSelected[1]]", dateSelectorParameters ? dateSelectorParameters.marks[plantDateIndexSelected[1]] : null);
        //console.log("071925 field", field);
        //console.log("071925 selectedFieldIncrement", selectedFieldIncrement);
        let _params = dateSelectorParameters ? dateSelectorParameters.marks[plantDateIndexSelected[1]] : null;
        //console.log("A071925 _params", _params ? _params : null);
        //console.log("A071925 selectedSeason", selectedSeason);
        //console.log("A071925 _params.props.children", _params ? _params.props.children : null);
        //console.log("A071925 _params.props.children", _params ? `${selectedSeason}-${_params.props.children}` : null);
        console.log("C072225 _params", _params);
        let _dateString = _params 
                    ?`${selectedSeason}-${_params.props.children[1].props.children}` 
                    : null;
        console.log("C072225 _dateString", _dateString);
        setDateString(_dateString);
        if (fieldsFiltered) {
            const polygonFeatures = fieldsFiltered.map((f: Field) => createFeatureFromField(f));
            const pointFeatures = fieldsFiltered.map((f: Field) => createCentroidFeatureFromField(f, _dateString));

            const polygonFeatureCollection = createFeatureCollection(polygonFeatures);
            const pointFeatureCollection = createFeatureCollection(pointFeatures);

            setFieldsDataSource(polygonFeatureCollection);
            setCentroidDataSource(pointFeatureCollection);
        }
    }, [fieldsFiltered, plantDateIndexSelected, selectedSeason]);

    useEffect(() => {
        console.log("D092425 selectedField", selectedField)
         if (selectedField){
             updateFieldSelected(selectedField)
         }
     }, [selectedField]);


     useEffect(() => {
        //console.log("090123 staticRasterDataSources", staticRasterDataSources)
        //let naip = overlayVisibility.find((item) => { return item.layer_id === NAIP_DATA_SOURCE });
        //updateLayersForExport(overlayVisibilityMapControl.concat(naip ? naip : []));
        const preppedForClipStaticRasterDataSources = staticRasterDataSources.map((source: DataSourceRasterLayer, sourceIndex:number) => {
            source.is_visible = true;
            return source;
        })
        //console.log("090423 preppedForClipStaticRasterDataSources", preppedForClipStaticRasterDataSources)
        updateLayersForExport(preppedForClipStaticRasterDataSources);
    }, [staticRasterDataSources]);

    /*useEffect(() => {
        console.log("090423 filteredDatesAvailable", filteredDatesAvailable)
        console.log("090423 datesAvailable", datesAvailable)
        console.log("090423 dwAndRasterTimelineDate", dwAndRasterTimelineDate)
        console.log("090423 timelineStatus", timelineStatus)
    }, [filteredDatesAvailable, datesAvailable, dwAndRasterTimelineDate, timelineStatus]);*/

    // zoom to a field when the field selection changes
    useEffect(() => {
        if (fields && field) {
            const selectedField = fields.find((f:any) => f.id == field);
            if (selectedField && width && height) {
                setSelectedFieldId(selectedField.id ? selectedField.id : null);
                setSelectedField(selectedField);
                setViewport({ ...zoomToFeature(createFeatureFromField(selectedField), viewport, width, height, 85), transitionDuration: 500 });
            }
        }
    }, [field, selectedFieldIncrement]);

    /*useEffect(() => {
        console.log("D111423 bbCardDataSources",  bbCardDataSources)
    }, [bbCardDataSources]);*/

    useEffect(() => {
        console.log("H111423 indexA", indexA)
    }, [indexA]);

    /*useEffect(() => {
        // Execute a query to get all relevant info for plant
        // onSoilProfileSetSelected(selectedSoilProfileSet);
        //console.log("selectedPlantId, selectedPlantName, selectedPlantCol, selectedPlantRow", selectedPlantId, selectedPlantName, selectedPlantCol, selectedPlantRow)
        console.log("AA091725 -_-_-_-_-_-_-_-_-_-_-_-_/// selectedPlantId, selectedPlantName, selectedPlantCol, selectedPlantRow", selectedPlantId, selectedPlantName, selectedPlantCol, selectedPlantRow)
        if (selectedPlantId){
            setSelectedPlantProfile({
                plantprofileId: selectedPlantId,
                plantprofileName: selectedPlantName,
                plantprofileCol: selectedPlantCol,
                plantprofileRow: selectedPlantRow,
            })
        }
    }, [selectedPlantId, selectedPlantName, selectedPlantCol, selectedPlantRow]);*/

    const delayedUrlUpdate = React.useCallback(_.debounce((url: any) => history.replace(url), 250), []);
    const onInteractionStateChange = (e: any) => {
        if (!e.isDragging && !e.isPanning && !e.isZooming) {
            const str = `@${viewport.latitude.toFixed(5)},${viewport.longitude.toFixed(5)},${viewport.zoom.toFixed(2)}`;
            const url = field ?
                generatePath(`${FIELD_APP_URL}/:vp/plants/:field`, { vp: str, field: field }) :
                generatePath(`${FIELD_APP_URL}/:vp/plants`, { vp: str });

            delayedUrlUpdate(url);
        }
    }

    const infoChildren = useMemo(() => {
        if (!selectedField) {
            return;
        }
        //console.log("selectedPlantProfile" , selectedPlantProfile)
        //console.log("A110923 selectedPlantObj" , selectedPlantObj)
        return (
            <AboveWidget 
                plantProfile={selectedPlantProfile}
                selectedField={selectedField}
                propertyUnitsData={propertyUnitsData}
                rasterDataSources={rasterDataSources}
                selectedPlantObj={selectedPlantObj}
                bbCardDataSources={bbCardDataSources}
                bbCardDataCounter={bbCardDataCounter}
                setBbCardDataCounter={setBbCardDataCounter}
                dateArrayIndexSelected={dateArrayIndexSelected}
                setDateArrayIndexSelected={setDateArrayIndexSelected}
                dateArrayDateStringSelected={dateArrayDateStringSelected}
                mapLayers={mapLayers}
                dateSelected={dwAndRasterTimelineDate}
                dwBBCardMinMaxData={dwBBCardMinMaxData}
            />
        )
    }, [mapLayers, selectedField, selectedPlantProfile, bbCardDataCounter, bbCardDataSources,/*dateArrayIndexSelected,*/ dateArrayDateStringSelected]);

    const zoomToExtent = React.useCallback(() => {
        if (width && height && fieldsDataSource) {
            deselectField();
            setViewport({ ...zoomToFeatures(fieldsDataSource, viewport, width, height, 100), transitionDuration: 500 });
        }
    }, [fieldsDataSource, viewport, width, height]);

    // field selection and hover
    const onSelectField = React.useCallback((field: Field) => {
        console.log("B091725 onSelectField", field);
        setSelectedFieldIncrement(prev => prev + 1); 
        setSelectedPlantId(null);
        setSelectedPlantName(null);
        setSelectedPlantCol(null);
        setSelectedPlantRow(null);
        setSelectedPlantObj(null);
        setSelectedPlantProfile(null);
        if (match) {
            history.push(`${match.url}${match.url.endsWith("/") ? "" : "/"}${field.id}`);
        }
    }, []);

    const onHoverField = React.useCallback((field: Field) => {
        setHoverFieldId(field ? field.id! : null);
    }, []);

    const deselectField = () => {
        console.log("C092425 deselectField");
        setSelectedFieldId(null);
        setSelectedField(null);
        if (match) {
            history.push(`${match.url}`);
        }
    };

    const onObjectClick = ({layer, object}: any) => {
        
        if(!object) {
            return;
        }
        //const obj = layer as any;
        let entity = layer.props.entity; //obj?._query?.entity
        //if(object.properties.hasOwnProperty('type') && object.properties.type === 'plant') { // plant
        console.log("B091725 layer", layer);
        console.log("B091725 object", object);
        console.log("B091725 dateString", dateString);
        setSelectedPlantObj(object)
        if(object.properties) {
            console.log("AA091725 setSelectedPlantProfile ************ ", {
                plantprofileId: object.properties[`${entity ? entity : 'plant'}_id`],
                plantprofileName: object.properties[`${entity ? entity : 'plant'}_name`],
                plantprofileCol: object.properties["Plant_value"],
                plantprofileRow: object.properties["Row_value"],
            })
            setSelectedPlantId(object.properties[`${entity ? entity : 'plant'}_id`])
            setSelectedPlantName(object.properties[`${entity ? entity : 'plant'}_name`])
            setSelectedPlantCol(object.properties["Plant_value"])
            setSelectedPlantRow(object.properties["Row_value"])
            if (object.properties[`${entity ? entity : 'plant'}_id`]){
                setSelectedPlantProfile({
                    plantprofileId: object.properties[`${entity ? entity : 'plant'}_id`],
                    plantprofileName: object.properties[`${entity ? entity : 'plant'}_name`],
                    plantprofileCol: object.properties["Plant_value"],
                    plantprofileRow: object.properties["Row_value"],
                    plantProfileYear: settings[SETTING_SEASON_YEAR],
                })
            }
        }

        if(object) {
            if(width && height && viewport.zoom < 18) {
                console.log("setViewport")
                setViewport({...zoomToFeature(
                    turf.buffer(object, 50, {units: 'meters'}), viewport, width, height, 100),
                    transitionDuration: 200 
                });
            }
        }
    }

    const onInfoDrawerClose = useCallback(() => {
        deselectField();
    }, []);

    const onHover = useCallback((event:any, mapLayers:any, _propertyUnitsData:any | null) => {
        //
        if(event.layer && event.object) {
            //console.log("y040223 hover > event", event);
            //console.log("y040223 hover > mapLayers", mapLayers);
            const object = event.object;
            const layer = event.layer;
            const id = layer.id;
            const mapLayer = mapLayers.find((layer:any) => (layer.id + (layer.dwFilters.version ? layer.dwFilters.version : "")) === id);
            if (mapLayer){
                //console.log("A112823 object", object);
                //console.log("A112823 _propertyUnitsData", _propertyUnitsData);
                const desc = mapLayer.getDescForObject(object, _propertyUnitsData, localePreferences, convertToPreferredUnitString);
                //console.log("041123 desc", desc);
                let desc2 = desc.split('\n').map((l: string, index:number) => {
                    return((l == '') ? <></> : <span key={`plant-s-${index}`}>
                        <span dangerouslySetInnerHTML={{ __html: l }} />
                        <br></br></span>)
                });
                setHoverInfo({
                    longitude: event.coordinate[0],
                    latitude: event.coordinate[1],
                    fieldName: desc2.filter((r: any) => r.key != null),
                });
            }else{
                setHoverInfo(null);
            }
        }else{
            setHoverInfo(null);
        }

    }, []);

    const fieldInfoChildren = useMemo(() => {
        if (!selectedField) {
            return;
        }
        /*console.log("022823 datesAvailable", datesAvailable);
        console.log("022823 filteredDatesAvailable", filteredDatesAvailable);
        console.log("022823 dateSelectorParameters", dateSelectorParameters);
        console.log("022823 timelineStatus", timelineStatus);
        console.log("022823 dwAndRasterTimelineDate", dwAndRasterTimelineDate);
        console.log("053023 selectedField", selectedField);
        console.log("A082423 dwAndRasterTimelineDate", dwAndRasterTimelineDate);
        console.log("A082423 selectedField", selectedField);
        console.log("L111523 visibleDW", visibleDW);*/
        return (
            <>
                { showDownload && <FieldDownload onCancel={() => setShowDownload(false)} /> }
                <GLMapLayers 
                    field={selectedField}
                    addMapLayers={addMapLayers} 
                    clearMapLayers={clearMapLayers} 
                    addMapLayer={addMapLayer} 
                    removeMapLayer={removeMapLayer}
                    refresh={() => {setRefreshAt(Date.now())}} 
                    dwAndRasterTimelineDate={dwAndRasterTimelineDate}
                    dwAndRasterTimelineDateComplete={dwAndRasterTimelineDateComplete}
                    visibleDW={visibleDW}
                    setVisibleDW={setVisibleDW}
                    setReadyToLoadSoilHorizonData={setReadyToLoadSoilHorizonData}
                    readyToLoadSoilHorizonData={readyToLoadSoilHorizonData}
                />
                {/*<StackedSoilLayer 
                    field={selectedField}
                    addMapLayer={addMapLayer} 
                    refresh={() => {setRefreshAt(Date.now())}} 
                />*/}  
                <L2ImagerySourceLayer 
                     field={selectedField}
                     addMapLayer={addMapLayer} 
                     refresh={() => {setRefreshAt(Date.now())}} 
                     fieldId={selectedFieldId}
                     rasterDataSources={rasterDataSources}
                     bbCardDataSources={bbCardDataSources}
                     setBbCardDataCounter={setBbCardDataCounter}
                     setRasterDataSources={setRasterDataSources}
                     setBbCardDataSources={setBbCardDataSources}
                     setPlantsCounter={setPlantsCounter}
                     setLoadedTiles={setLoadedTiles}
                     loadedTiles={loadedTiles}
                     dateSelectorParameters={dateSelectorParameters}
                     setDateSelectorParameters={setDateSelectorParameters}
                     activeAnimation={activeAnimation}
                     setActiveAnimationFromParent={setActiveAnimationFromParent}
                     animationRasterDataSources={animationRasterDataSources}
                     setAnimationRasterDataSources={setAnimationRasterDataSources}
                     UASImageryAnimationRasterDataSources={UASImageryAnimationRasterDataSources}
                     setUASImageryAnimationRasterDataSources={setUASImageryAnimationRasterDataSources}
                     staticRasterDataSources={staticRasterDataSources}
                     setStaticRasterDataSources={setStaticRasterDataSources}
                     setDatesAvailable={setDatesAvailable}
                     datesAvailable={datesAvailable}
                     visible={visible} 
                     setVisible={setVisible}
                     timelineStatus={timelineStatus}
                     setTimelineStatus={setTimelineStatus}
                     filteredDatesAvailable={filteredDatesAvailable}
                     setFilteredDatesAvailable={setFilteredDatesAvailable}
                     setDwAndRasterTimelineDate={setDwAndRasterTimelineDate}
                     setDwAndRasterTimelineDateComplete={setDwAndRasterTimelineDateComplete}
                     dwAndRasterTimelineDate={dwAndRasterTimelineDate}
                     visibleDW={visibleDW}
                     setVisibleDW={setVisibleDW}        
                     plantDateIndexSelected={plantDateIndexSelected}
                     setPlantDateIndexSelected={setPlantDateIndexSelected}
                />  
                <SoilDataSourceLayer
                     field={selectedField}
                     rampWidth={240}
                     addMapLayer={addMapLayer} 
                     refresh={() => {setRefreshAt(Date.now())}} 
                     fieldId={selectedFieldId}
                     soilRasterDataSources={soilRasterDataSources}
                     setSoilRasterDataSources={setSoilRasterDataSources}
                     setSoilRasterVisibility={setSoilRasterVisibility}
                     soilRasterVisibility={soilRasterVisibility}
                     selectedSeason={selectedSeason}
                     //key={updateSoilsKey}
                     setUpdateSoilsKey={setUpdateSoilsKey}
                     readyToLoadSoilHorizonData={readyToLoadSoilHorizonData}
                     setReadyToLoadSoilHorizonData={setReadyToLoadSoilHorizonData}
                     context={"plantsView"}
                     horizonSelected={horizonSelected}
                     setHorizonSelected={setHorizonSelected}
                     propertySelected={propertySelected}
                     setPropertySelected={setPropertySelected}
                />
                <SoilInfoWidget field={selectedField} customCSS={"scrollX"} />
            </>
        )
    }, [showDownload, soilRasterDataSources, bbCardDataSources, bbCardDataCounter, soilRasterVisibility, 
        selectedField, width, height, loadedTiles, activeAnimation, datesAvailable, visible, dateSelectorParameters,
        timelineStatus, dwAndRasterTimelineDate, visibleDW, selectedSeason, readyToLoadSoilHorizonData /*mapLayers*/]);

    const layers = mapLayers.map((layer: MapLayer) => {
        const obj = layer as any;
        const provider = obj as IDeckGlLayer;
        return provider.getDeckGlLayer(viewport.zoom, selected, setSelected, obj?._query?.entity);
    });
    
    //layers.reverse(); // Get raster layers to be below point layers

    function assessAnimationLoad(){
        if (map){
            if (map.loaded() == true && map.areTilesLoaded() == true) {
                console.log("load is complete");
                //setActiveAnimationFromParent("playing");
                setLoadedTiles(true);
                playAnimation(filteredDatesAvailable);
            } else {
                console.log("load is incomplete");
                setTimeout(function() {
                    assessAnimationLoad();
                },500);
            }
        }
    }

    function setActiveAnimationFromParent(_settingToApply:string){
        setActiveAnimation(_settingToApply)
    }

    function playAnimation(_datesAvailable:Array<string>){
        if (animationInterval){
            clearInterval(animationInterval);
        }
        if (indexA + 1 >= filteredDatesAvailable.length){
            setIndexA(0);
        }
        setAnimationInterval(setInterval(myTimer, 1000));

        function myTimer() {
            setTimerInterval(prev => prev + 1 )
        }
    }

    return (
        <div className="d-flex h-100 position-relative">
            <div className="bg-light border border-bottom-0 border-dark border-left-0 border-top-0 d-flex flex-column flex-shrink-0 rounded-left text-dark" style={{ width: '200px' }}>
                <FieldList
                    title="Digital Vegetation Signature"
                    fields={fields}
                    fieldsFiltered={fieldsFiltered}
                    selectedId={selectedFieldId}
                    onSelectField={onSelectField}
                    onHoverField={onHoverField}
                />
            </div>
            {selectedField &&
                <div className="bg-light border border-bottom-0 border-left-0 border-right border-secondary border-top-0 d-flex flex-column flex-shrink-0 rounded-right text-dark" style={{ width: '295px' }}>
                    <InfoDrawer 
                        field={selectedField} 
                        onClose={onInfoDrawerClose}
                        setShowDownload={setShowDownload}
                    >
                        {fieldInfoChildren}
                    </InfoDrawer>
                </div>
            }
            <div className="flex-fill bg-white position-relative" ref={mapContainerRef}>
                <DeckGL
                    key={`${plantsCounter}-map`}
                    layers={[...layers]}
                    viewState={{
                        ...viewport,
                        maxZoom:25
                    }}
                    controller={true}
                    ContextProvider={MapContext.Provider}
                    onViewStateChange={(v: any) => setViewport(v.viewState)}
                    //getTooltip={getTooltip}
                    onHover={(e:any) => onHover(e,mapLayers, propertyUnitsData)}
                    onClick={(e:any) => onObjectClick(e)}
                    getCursor={({isHovering}:any) => isHovering ? 'pointer' : 'grab'}
                    onInteractionStateChange={onInteractionStateChange}
                    glOptions={{stencil: true}}
                >
                    <StaticMap 
                        {...viewport} 
                        width="100%" height="100%" 
                        mapStyle={ESRI_BASE} 
                        preventStyleDiffing={true}
                        ref={(ref:any) => ref && setMap(ref.getMap())}
                    >
                        <BaseMapLayers 
                            fields={fieldsFiltered ? fieldsFiltered : fields} 
                            selectedFieldId={selectedFieldId} 
                            hoverFieldId={hoverFieldId} 
                            dateString={dateString}
                            />
                    </StaticMap>   
                {/*console.log("A111423 soilRasterDataSources", soilRasterDataSources)}    
                {console.log("A111423 rasterDataSources", rasterDataSources)}    
                {console.log("A111423 dataSources", dataSources)}   
                {console.log("A111423 staticRasterDataSources", staticRasterDataSources)*/}  
                {rasterDataSources 
                    ? _.orderBy(rasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer) => (
                        <Source key={s.uuid} id={s.uuid} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={s.uuid}
                                beforeId="GROUP_baselayer"
                                id={s.uuid}
                                type='raster'
                                source={s.uuid}
                                maxzoom={s.max_zoom} minzoom={s.min_zoom}
                                layout={{ visibility: overlayVisibility.find((item) => { return item.layer_id === s.id })?.is_visible ? 'visible' : 'none' }}
                            />
                        </Source>)) 
                    : null}      
                {/*console.log("A111423 UASImageryAnimationRasterDataSources", UASImageryAnimationRasterDataSources)*/}
                {UASImageryAnimationRasterDataSources 
                    ? _.orderBy(UASImageryAnimationRasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer, index:number) => (
                        <Source key={`${s.uuid}_animation_base_source`} id={`${s.uuid}`} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={`${s.uuid}_animation_base`}
                                beforeId="GROUP_baselayer"
                                id={`${s.uuid}`}
                                type='raster'
                                source={s.uuid}
                                maxzoom={24}
                                minzoom={s.min_zoom}
                                layout={{ visibility: (activeAnimation === "playing" || activeAnimation === "paused")
                                        ? visible 
                                            ? overlayVisibility.find((item) => { return item.layer_id === (s.id).split("_")[0] })?.is_visible ? 'visible' : 'none'
                                            : 'none'
                                        : 'none'
                                    }}
                                paint={{
                                    'raster-opacity': s.opacity,
                                }}
                            />
                        </Source>)) 
                    : null}     
                {/*console.log("animationRasterDataSources", animationRasterDataSources)*/}  
                {animationRasterDataSources 
                    ? _.orderBy(animationRasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer, index:number) => (
                        <Source key={`${s.uuid}`} id={`${s.uuid}`} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={`${s.uuid}`}
                                beforeId="GROUP_baselayer"
                                id={`${s.uuid}`}
                                type='raster'
                                source={s.uuid}
                                maxzoom={24}
                                minzoom={s.min_zoom}
                                layout={{ visibility: (activeAnimation === "playing" || activeAnimation === "paused")
                                        ? visible 
                                            ? 'visible'
                                            : 'none'
                                        : 'none'
                                    }}
                                paint={{
                                    'raster-opacity': s.opacity,
                                }}
                            />
                        </Source>)) 
                    : null}                     
                {/*console.log("staticRasterDataSources", staticRasterDataSources)*/}  
                {staticRasterDataSources 
                    ? _.orderBy(staticRasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer, index:number) => (
                        <Source key={`${s.uuid}`} id={`${s.uuid}`} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={`${s.uuid}`}
                                beforeId="GROUP_baselayer"
                                id={`${s.uuid}`}
                                type='raster'
                                source={s.uuid}
                                maxzoom={24}
                                minzoom={s.min_zoom}
                                layout={{ visibility: (activeAnimation === "playing" || activeAnimation === "paused")
                                        ? loadedTiles
                                            ? 'none'
                                            : visible 
                                                ? 'visible'
                                                : 'none'
                                        : visible
                                            ? 'visible'
                                            : 'none'
                                    }}
                            />
                        </Source>)) 
                    : null}  
                {/*console.log("121423 soilRasterDataSources", soilRasterDataSources)*/}  
                {soilRasterDataSources ? 
                    _.orderBy(soilRasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer) => (
                        <Source key={s.uuid} id={s.uuid} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={s.uuid}
                                //beforeId={rasterDataSources.find((item) => item.name === "UAS Imagery")?.uuid}
                                beforeId={staticRasterDataSources ? staticRasterDataSources[0]?.uuid : "GROUP_baselayer"}
                                id={s.uuid}
                                type='raster'
                                source={s.uuid}
                                maxzoom={24} //{s.max_zoom} 
                                minzoom={0} //{s.min_zoom}
                                layout={ { visibility: soilRasterVisibility ? 'visible' : 'none'} }
                            />
                        </Source>)
                ) : null} 
                {/*console.log("AAA053023 rasterLayers", rasterLayers)*/}
                {rasterLayers ? _.orderBy(rasterLayers, ['order'], ['desc']).map((s: DataSourceRasterLayer) => (
                        <Source key={s.id} id={s.id} type='raster' tileSize={256} scheme="tms" tiles={s.tiles}>
                            <Layer
                                key={s.id}
                                beforeId="GROUP_fieldlayer"
                                id={s.id}
                                type='raster'
                                source={s.id}
                                maxzoom={s.max_zoom}
                                minzoom={s.min_zoom}
                                layout={{ visibility: 'visible' }}
                            />
                        </Source>)
                    ) : null}           
                    <ScaleControl 
                        style={{right: (selectedPlantProfile && !rightInfoDrawerOpen) ? 50 : 10, top: 15}} 
                        maxWidth={200} 
                        unit={preferredLenAbbr === "in" ? "imperial" : "metric"} // Change to "metric" or "imperial" as needed
                    />
                    { hoverInfo
                        ? <Popup
                            key={`plant-popup-${hoverInfo ? hoverInfo.longitude : "null"}`}
                            longitude={hoverInfo ? hoverInfo.longitude : 0}
                            latitude={hoverInfo ? hoverInfo.latitude : 0}
                            closeButton={false}
                            className="field-info"
                        >
                            {hoverInfo ? hoverInfo.fieldName : ""}
                        </Popup>
                        : <></>
                    }
                    <NavigationControl style={{left: 10, top: 10}} showCompass={false} /> 
                    <ZoomExtentTool zoomToExtent={zoomToExtent} /> 
                </DeckGL>
                                  
                <div style={{position: 'absolute', bottom:'unset', top:'47px', right:'10px'}}>
                    <LayerVisibilityControlPanel 
                        showDatePicker={true} 
                        layers={overlayVisibility} 
                        onChange={(visibility: LayerVisibility[]) => setOverlayVisible(visibility)}
                        initiallyOpen={false}
                    />
                </div>
                <div id="l2ImageryTimeline" style={{
                        height: '260px',
                        width: 'calc(100% - 20px)',
                        backgroundColor:'transparent',
                        position: 'absolute',
                        bottom: '0px',
                        pointerEvents: 'none',
                        marginLeft: '10px', 
                }}
                ></div>
            </div>
            { (selectedPlantProfile && rightInfoDrawerOpen)
               ? <div className="bg-light border border-bottom-0 border-top-0 border-right-0 d-flex flex-column flex-shrink-0 rounded-left text-dark" style={{ width: '500px' }}>
                    <InfoDrawer field={null} onClose={() => { setRightInfoDrawerOpen(!rightInfoDrawerOpen) }} showClose={true} side={"right"}>
                        {infoChildren}
                    </InfoDrawer>
                </div>
                : selectedPlantProfile 
                    ? <div className="closed-widget-button">
                        <Button 
                            onClick={() => { setRightInfoDrawerOpen(!rightInfoDrawerOpen) }}
                            className="rounded-circle" variant="light" size="sm" title="Close Card" style={{cursor:'pointer', height:'30px', width:'30px'}}>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </Button>
                    </div>
                    : <></>
            }
        </div>
    );
}
